import {
    EpsonAlign,
    EpsonBarcode,
    EpsonColor,
    EpsonCut,
    EpsonDrawer,
    EpsonFont,
    EpsonHRI,
    EpsonLevel,
    EpsonPulse,
    EpsonSymbol,
    NFSendResult,
    NFThermalPrinter,
} from 'src/app/shared/model/nf-thermal-printer.model';

import {
    NFThermalDriverBase,
    NFThermalPrinterConfig
} from './nf-thermal-driver-base';

import {
    XMLNode
} from 'src/app/shared/XmlNode';

/**
 * This class implements the NFThermalPrinter interface for rendering to an HTML DOM element.
 */
export class NFHtmlDriver extends NFThermalDriverBase implements NFThermalPrinter {
    private currentLineConfig?: { styles: Record<string, string> };
    private currentDocument?: XMLNode;
    private documents: string[] = [];
    private domParser = new DOMParser();
    private lineBuffer: XMLNode[] = []; 

    /**
     * @constructor
     * @param {NFHtmlDriverConfig} [configuration] - Optional configuration settings.
     */
    constructor(ipAddress: string, port?: number, configuration?: NFThermalPrinterConfig) {
        super(ipAddress, 0, configuration);

        this.currentDocument = this.getRootNode()
    }

    /**
     * Creates and returns the root XMLNode for a new print document.
     * @returns {XMLNode} The root XMLNode.
     */
    private getRootNode() {
        return new XMLNode('div', {
            style: this.getStylesString({
                'font-family': 'monospace',
                'font-size': '16px',
                'display': 'flex',
                'flex-direction': 'column',
                'letter-spacing': '0.5px',
                'overflow-x': 'hidden',
                'width': '488px',
                'word-break': 'break-all'
            })
        });
    }

    /**
     * Finalizes the current print document, converts it to HTML, and adds it to the list of documents.
     */
    private finalizeDocument() {
        if(!this.currentDocument) {
            return;
        }
        
        const doc = this.domParser.parseFromString(this.currentDocument.toString(), 'text/html');
        const content = doc.getElementsByTagName('body')[0]?.children[0];

        if(content) {
            this.documents.push(content.outerHTML);
        }

        this.currentDocument = undefined;
    }

    /**
     * Converts a JavaScript object of CSS styles into a CSS style string.
     * @param {Record<string, string>} styles - An object where keys are CSS properties and values are their corresponding values.
     * @returns {string} A CSS style string suitable for the 'style' attribute of an HTML element.
     */
    private getStylesString(styles: Record<string, string>) {
        return Object.entries(styles).reduce((acc, [key, value]) => acc + `${key}:${value}; `, '');
    }

    /**
     * Calculates the current height scale based on printer settings.
     * @returns {number} The height scale factor.
     */
    private getCurrentHeightScale() {
        return (this.printSettings.height * (this.printSettings.dh ? 2 : 1));
    }

    /**
     * Calculates the current width scale based on printer settings.
     * @returns {number} The width scale factor.
     */
    private getCurrentWidthScale() {
        return (this.printSettings.width * (this.printSettings.dw ? 2 : 1));
    }

    /**
     * Generates a CSS style string based on the current print settings.
     * Applies current print settings to a given style object.
     * @private
     * This method translates Epson printer settings into CSS styles.
     * @returns {string} A string containing the styles
     */
    private getCurrentStyles() {
        const styles: Record<string, string> = {};

        styles['white-space'] = 'pre-wrap';

        if (this.printSettings.ul) {
            styles['text-decoration'] = 'underline';
        }

        if (this.printSettings.em) {
            styles['font-weight'] = 'bold';
        }

        // Text styles
        if (this.printSettings.reverse) {
            styles['background-color'] = 'black';
            styles['color'] = 'white';
        } else {
            switch (this.printSettings.color) {
                case EpsonColor.COLOR_1:
                    styles['color'] = 'black';
                    break;
                case EpsonColor.COLOR_2:
                    styles['color'] = 'red';
                    break;
                default:
                    styles['color'] = 'black';
                    break;
            }
        }

        return this.getStylesString(styles);
    }

    /**
     * Generates a CSS style object for a row container based on current print settings.
     * @returns {Record<string, string>} A JavaScript object containing CSS styles for a row container.
     */
    public getRowContainerStyle() {
        const heightScale = this.getCurrentHeightScale();
        const widthScale = this.getCurrentWidthScale();

        const styles: Record<string, string> = {};

        switch (this.printSettings.align) {
            case EpsonAlign.CENTER:
                styles['text-align'] = 'center';
                break;
            case EpsonAlign.RIGHT:
                styles['text-align'] = 'right';
                break;
            case EpsonAlign.LEFT:
            default:
                break;
        }

        //Double width/height
        if (heightScale > 1 || widthScale > 1) {
            styles['transform'] = `scale(${widthScale}, ${heightScale})`;
            styles['display'] = 'inline-block';
            styles['width'] = `${100 / widthScale}%`;

            const transformOrigin: string[] = [];

            if (heightScale > 1) {
                transformOrigin.push('top');
            }

            if (widthScale > 1) {
                transformOrigin.push('left');
            }

            if (transformOrigin.length > 0) {
                styles['transform-origin'] = transformOrigin.join(' ');
            }
        }

        return styles;
    }

    /**
     * Adds text to the print document.
     * @param {string} text - The text to be added.
     */
    public addText(text: string) {
        if (this.driverConfig.eReceiptMode) {
            return;
        }

        if(!this.currentDocument) {
            this.currentDocument = this.getRootNode();
        }

        if(!text) {
            return;
        }

        const normalizedText = text.replace(/\r\n|\n\r|\r|\n/g, '\n');
        const lines = normalizedText.split('\n');

        // Remove exceeding newline
        if(normalizedText.startsWith('\n')) {
            lines.shift();
        }

        const currentStyles = this.getCurrentStyles();

        for(const line of lines) {
            if(!this.currentLineConfig) {
                this.currentLineConfig = {
                    styles: this.getRowContainerStyle()
                };
            }

            if(line) { // Text line
                this.lineBuffer.push(XMLNode.fromObject({
                    name: 'span',
                    attributes: { style: currentStyles },
                    content: line
                }));
            } else { // Newline
                const textLength = this.lineBuffer.reduce((acc, child) => acc + (child.getContent()?.length ?? 0), 0);
                const styles = this.currentLineConfig.styles;
                const heightScale = this.getCurrentHeightScale();

                if(!textLength) {
                    //Make container size a single line since there is no content
                    styles['height'] = `24px`;
                    delete styles['transform'];
                    delete styles['transform-origin'];
                    delete styles['display'];
                    delete styles['width'];
                } else if (heightScale > 1) {
                    const charsPerLine = (this.driverConfig.columns ?? 48) / this.getCurrentWidthScale();
                    const rows = Math.ceil(textLength / charsPerLine);

                    styles['height'] = `${24 * rows * heightScale}px`;
                }

                const lineContainer = new XMLNode('div', {
                    style: this.getStylesString(styles)
                });

                for(const child of this.lineBuffer) {
                    lineContainer.appendChild(child);
                }

                this.currentDocument.appendChild(lineContainer);
                this.currentLineConfig = undefined;
                this.lineBuffer = [];
            }
        }
    }

    /**
     * Adds a logo to the print document.
     * For HTML, this would typically be an image. We'll use a placeholder.
     * @param {number} key1 - The first key of the logo (unused in HTML rendering directly).
     * @param {number} key2 - The second key of the logo (unused in HTML rendering directly).
     */
    public addLogo(key1: number, key2: number) {
    }

    /**
     * Adds a barcode to the print document.
     * For HTML, this typically requires a library to render barcodes.
     * Here, we'll use a placeholder or simply display the data.
     * @param {string} data - The data to be encoded in the barcode.
     * @param {EpsonBarcode} type - The type of barcode.
     * @param {EpsonHRI} [hri] - The human-readable interpretation of the barcode.
     * @param {EpsonFont} [font] - The font of the barcode.
     * @param {number} [width] - The width of the barcode.
     * @param {number} [height] - The height of the barcode.
     */
    public addBarcode(data: string, type: EpsonBarcode, hri?: EpsonHRI, font?: EpsonFont, width?: number, height?: number) {
        if (this.driverConfig.eReceiptMode) {
            return;
        }
    }

    /**
     * Adds a symbol (like QR code) to the print document.
     * Similar to barcodes, this requires a library for proper rendering.
     * @param {string} data - The data to be encoded in the symbol.
     * @param {EpsonSymbol} type - The type of symbol.
     * @param {EpsonLevel} [level] - The error correction level of the symbol.
     * @param {number} [width] - The width of the symbol.
     * @param {number} [height] - The height of the symbol.
     * @param {number} [size] - The size of the symbol.
     */
    addSymbol(data: string, type: EpsonSymbol, level?: EpsonLevel, width?: number, height?: number, size?: number) {
        if (this.driverConfig.eReceiptMode) {
            return;
        }
    }

    /**
     * Adds a cut command to the print document.
     * For HTML, this can be represented as a horizontal rule or a visual separator.
     * @param {EpsonCut} [type] - The type of cut.
     */
    public addCut(type?: EpsonCut) {
        if (this.driverConfig.eReceiptMode) {
            return;
        }

        if(!this.currentDocument) {
            this.currentDocument = this.getRootNode()
        }
        
        if(this.driverConfig.splitDocumentsOnCut) {
            this.finalizeDocument();
        } else {
            this.currentDocument.appendChild(XMLNode.fromObject({
                name: 'hr',
                attributes: {
                    style: 'border-top: 1px dashed #ccc; margin: 20px 0;'
                }
            }));
        }
    }

    /**
     * Adds a pulse command to the print document.
     * This action has no direct visual equivalent in HTML, so we can log it or represent it textually.
     * @param {EpsonDrawer} [drawer] - The drawer to be pulsed.
     * @param {EpsonPulse} [time] - The pulse time.
     */
    public addPulse(drawer?: EpsonDrawer, time?: EpsonPulse) {
    }

    /**
     * Sends the generated HTML content to the registered print callback, if any.
     * If no callback is set, it resolves immediately with 'PRINTED'.
     * @returns {Promise<NFSendResult>} A promise that resolves to 'PRINTED' on success or 'CONNECTION_ERROR' if the callback fails.
     * @override
     */
    public async send(): Promise<NFSendResult> {
        if (this.driverConfig.eReceiptMode) {
            return 'PRINTED';
        }

        if(this.currentDocument) {
            this.finalizeDocument();
        }

        if (this.driverConfig.printCallback) {
            return this.driverConfig.printCallback({
                documents: [...this.documents]
            }).then(
                () => 'PRINTED',
                (err) => err || 'CONNECTION_ERROR'
            );
        }

        return 'PRINTED';
    }

    /**
     * Attempts to connect to a printer and returns its connection status.
     * In the context of this driver, this method will always return connected.
     * @param {string} ip - The IP address of the printer (not applicable for HTML driver).
     * @param {number} port - The port number of the printer (not applicable for HTML driver).
     * @returns {Promise<{ip: string; connected: boolean}>} An object containing the printer IP and connection status
     */
    public static async connectAttempt(ip: string, port: number): Promise<{ ip: string; connected: boolean }> {
        return Promise.resolve({ ip: ip, connected: true });
    }
}